import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { ApplicantType, CertificateOfOrigin, CoOVerifyResult, OriginCriterion, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'apply', label: 'Apply' },
  { key: 'mine', label: 'My certificates' },
  { key: 'all', label: 'All certificates (staff)' },
  { key: 'verify', label: 'Verify a certificate' },
] as const;

export function Certificates() {
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M12_ROLES.TRADE_OFFICER);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('apply');

  return (
    <div>
      <PageHeader title="Certificates of Origin" subtitle="Tariff-scheme (AfCFTA/GSP/EU) origin certification — validated synchronously against the configured origin-criteria rules." />
      <Tabs tabs={isOfficer ? TABS : TABS.filter((t) => t.key !== 'all')} value={tab} onChange={setTab} />
      {tab === 'apply' ? <ApplyForm /> : tab === 'mine' ? <MineCertificates /> : tab === 'all' ? <AllCertificates /> : <Verify />}
    </div>
  );
}

function ApplyForm() {
  const { persona } = useAuth();
  const options: Array<{ type: ApplicantType; id: string; label: string }> = [];
  if (persona?.nin) options.push({ type: 'PERSON', id: persona.nin, label: `Myself (${persona.nin})` });
  if (persona?.actingForBusinessId) options.push({ type: 'ENTITY', id: persona.actingForBusinessId, label: `My business (${persona.actingForBusinessId})` });

  // Hooks called unconditionally — see Permits.tsx's ApplyForm for why.
  const [applicantIdx, setApplicantIdx] = useState(0);
  const [consignmentDescription, setConsignmentDescription] = useState('');
  const [hsCodes, setHsCodes] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [scheme, setScheme] = useState<'AFCFTA' | 'GSP' | 'EU'>('AFCFTA');
  const [originCriterion, setOriginCriterion] = useState<OriginCriterion>('WHOLLY_OBTAINED');
  const [localValueAddedPct, setLocalValueAddedPct] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [grossWeightKg, setGrossWeightKg] = useState('');
  const [netWeightKg, setNetWeightKg] = useState('');
  const [packagesCount, setPackagesCount] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CertificateOfOrigin | null>(null);

  if (!persona) return <InfoNote message="Sign in to apply for a certificate of origin." />;
  if (options.length === 0) return <InfoNote message="Your account has no NIN or business mandate on file — sign in as a trader/investor persona." />;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const applicant = options[applicantIdx]!;
      const res = await api.post<CertificateOfOrigin>(
        '/api/v1/m12/certificates-of-origin',
        {
          exporterType: applicant.type,
          exporterId: applicant.id,
          consignmentDescription,
          hsCodes: hsCodes.split(',').map((s) => s.trim()).filter(Boolean),
          destinationCountry: destinationCountry.toUpperCase(),
          scheme,
          originCriterion,
          localValueAddedPct: originCriterion === 'VALUE_ADDED' && localValueAddedPct ? Number.parseFloat(localValueAddedPct) : undefined,
          invoiceNumber,
          invoiceDate,
          grossWeightKg: Number.parseFloat(grossWeightKg),
          netWeightKg: Number.parseFloat(netWeightKg),
          packagesCount: Number.parseInt(packagesCount, 10),
        },
        { actingFor: applicant.type === 'ENTITY' ? applicant.id : undefined, idempotencyKey: newIdempotencyKey() },
      );
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Application failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className={`card p-5 ${created.state === 'REJECTED' ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
        <h2 className={`text-sm font-bold ${created.state === 'REJECTED' ? 'text-rose-800' : 'text-emerald-800'}`}>{created.refNo} — {created.state}</h2>
        {created.rejectionReason ? <p className="mt-1 text-xs text-rose-700">{created.rejectionReason}</p> : (
          <p className="mt-1 text-xs text-emerald-700">Origin criterion validated against the {created.scheme} rules for this HS chapter — a certification fee intent has been raised.</p>
        )}
        <button className="btn-secondary mt-3 !bg-white" onClick={() => setCreated(null)}>Apply for another</button>
      </div>
    );
  }

  return (
    <div className="card p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        {options.length > 1 ? (
          <Field label="Applying as">
            <select className="field-input" value={applicantIdx} onChange={(e) => setApplicantIdx(Number(e.target.value))}>
              {options.map((o, i) => <option key={o.id} value={i}>{o.label}</option>)}
            </select>
          </Field>
        ) : null}
        <Field label="Consignment description"><textarea className="field-input" rows={2} value={consignmentDescription} onChange={(e) => setConsignmentDescription(e.target.value)} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="HS codes (comma separated)"><input className="field-input" value={hsCodes} onChange={(e) => setHsCodes(e.target.value)} placeholder="6109.10" /></Field>
          <Field label="Destination (ISO2)"><input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value.toUpperCase())} maxLength={2} /></Field>
          <Field label="Scheme">
            <select className="field-input" value={scheme} onChange={(e) => setScheme(e.target.value as typeof scheme)}>
              <option value="AFCFTA">AfCFTA</option><option value="GSP">GSP</option><option value="EU">EU</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Origin criterion">
            <select className="field-input" value={originCriterion} onChange={(e) => setOriginCriterion(e.target.value as OriginCriterion)}>
              <option value="WHOLLY_OBTAINED">Wholly obtained</option>
              <option value="CHANGE_TARIFF_HEADING">Change of tariff heading</option>
              <option value="VALUE_ADDED">Value added</option>
            </select>
          </Field>
          {originCriterion === 'VALUE_ADDED' ? (
            <Field label="Local value-added %"><input className="field-input" type="number" value={localValueAddedPct} onChange={(e) => setLocalValueAddedPct(e.target.value)} /></Field>
          ) : null}
        </div>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Invoice number"><input className="field-input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} /></Field>
          <Field label="Invoice date"><input className="field-input" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} /></Field>
          <Field label="Gross weight (kg)"><input className="field-input" type="number" value={grossWeightKg} onChange={(e) => setGrossWeightKg(e.target.value)} /></Field>
          <Field label="Net weight (kg)"><input className="field-input" type="number" value={netWeightKg} onChange={(e) => setNetWeightKg(e.target.value)} /></Field>
        </div>
        <Field label="Packages" className="max-w-[8rem]"><input className="field-input" type="number" min="1" value={packagesCount} onChange={(e) => setPackagesCount(e.target.value)} /></Field>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={busy || !consignmentDescription || hsCodes.trim().length === 0 || destinationCountry.length !== 2 || !invoiceNumber || !invoiceDate || !grossWeightKg || !netWeightKg}
        >
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </div>
  );
}

function MineCertificates() {
  const { persona } = useAuth();
  const mineQ = useApiQuery<{ items: CertificateOfOrigin[] }>('/api/v1/m12/certificates-of-origin/me', persona?.actingForBusinessId);
  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No certificates yet" /> : (
        <div className="space-y-2">
          {mineQ.data!.items.map((c) => (
            <Link key={c.id} to={`/trade/certificates/${c.refNo}`} className="card block p-4 hover:border-brand-300">
              <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold">{c.refNo}</span><StatusBadge status={c.state} /></div>
              <div className="mt-1 text-xs text-slate-500">{c.scheme} · {c.consignmentDescription}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AllCertificates() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const listQ = useApiQuery<Page<CertificateOfOrigin>>(`/api/v1/m12/certificates-of-origin?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['SUBMITTED', 'VALIDATED', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'WITHDRAWN', 'REVOKED'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ref no, exporter, cert serial…" /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No certificates match" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Ref</th><th className="table-head">Scheme</th><th className="table-head">Exporter</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs"><Link className="text-brand-600 hover:underline" to={`/trade/certificates/${c.refNo}`}>{c.refNo}</Link></td>
                  <td className="table-cell text-xs">{c.scheme}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{c.exporterId}</td>
                  <td className="table-cell"><StatusBadge status={c.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Verify() {
  const [serial, setSerial] = useState('');
  const [result, setResult] = useState<CoOVerifyResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.get<CoOVerifyResult>(`/api/v1/m12/certificates-of-origin/verify/${encodeURIComponent(serial)}`));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed — this tool requires a Trade Officer or service token.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Sey-X style verify-by-serial</h2>
      <p className="mb-3 text-xs text-slate-500">For foreign customs / staff verification — requires a Trade Officer role.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="flex gap-2">
        <input className="field-input" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Certificate serial…" />
        <button className="btn-primary shrink-0" onClick={check} disabled={busy || !serial}>{busy ? 'Checking…' : 'Verify'}</button>
      </div>
      {result ? (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
          {result.found ? (
            <>
              <div className="flex items-center gap-2"><StatusBadge status={result.valid ? 'ACTIVE' : 'EXPIRED'} /> {result.scheme} · {result.state}</div>
              <div className="mt-1 text-xs text-slate-500">Destination: {result.destinationCountry} · Valid to: {formatDate(result.validTo)}</div>
            </>
          ) : (
            <span className="text-slate-500">No certificate found with that serial.</span>
          )}
        </div>
      ) : null}
    </div>
  );
}
